import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/onix/Seo";
import { SectionEyebrow } from "../components/onix/SectionEyebrow";
import { Reveal } from "../components/onix/Reveal";
import { CTASection } from "../components/onix/CTASection";
import { DisclaimerBlock } from "../components/onix/DisclaimerBlock";
import { CinematicScene } from "../components/onix/CinematicScene";
import { getTools, getPillars } from "../data/tools";
import { useLang } from "../i18n/LanguageContext";

const ECO_SCENES = {
  "onix-messenger": "eco_messenger",
  "onix-vision": "eco_vision",
  "onix-tactical-graph": "eco_graph",
  "onix-malware-static-analyzer": "eco_malware",
  "onix-autosoc-siem": "eco_autosoc",
  "sensorizacion-ciberdefensa": "eco_sensor",
  "dwi-onix": "eco_dwi",
};

export default function Ecosistema() {
  const { lang, t } = useLang();
  const te = t.eco;
  const tc = t.common;
  const tools = getTools(lang);
  const pillars = getPillars(lang);
  return (
    <main className="pt-[76px]">
      <Seo title={te.seoTitle} description={te.seoDesc} path="/ecosistema" />

      <section className="onix-shell py-20 md:py-32 border-b border-onix-line">
        <Reveal>
          <SectionEyebrow index="01" label={te.label} />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-serif leading-[1.05] text-onix-white max-w-4xl mb-8">
            {t.home.s1Title}
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-onix-silver max-w-2xl">
            {t.home.s1Text}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-px bg-onix-line mt-16 border border-onix-line">
            {pillars.map((p) => (
              <div key={p.num} className="bg-onix-black p-5 min-h-[110px] flex flex-col justify-between">
                <span className="tech-label">{p.num}</span>
                <span className="font-sans font-bold text-[11px] md:text-xs tracking-[0.12em] text-onix-white">{p.name}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section id="herramientas" className="pt-20 md:pt-32">
        <div className="onix-shell">
          <Reveal>
            <SectionEyebrow index="02" label={te.toolsLabel} />
          </Reveal>
        </div>
        {tools.map((tool) => {
          const scene = ECO_SCENES[tool.slug];
          return (
            <CinematicScene
              key={tool.slug}
              img={`/assets/img/${scene}.webp`}
              imgMobile={`/assets/img/${scene}_m.webp`}
              data-testid={`eco-scene-${tool.slug}`}
            >
              <div className="onix-shell py-16 md:py-24">
                <Reveal>
                  <article data-testid={`eco-tool-${tool.slug}`} className="grid lg:grid-cols-12 gap-8 border-t border-onix-line pt-12">
                    <div className="lg:col-span-4">
                      <span className="tech-label block mb-5">{tool.num}</span>
                      <h2 className="font-sans font-bold text-sm tracking-[0.14em] uppercase text-onix-white mb-4">{tool.name}</h2>
                    </div>
                    <div className="lg:col-span-8">
                      <h3 className="font-serif font-bold text-2xl md:text-3xl leading-snug text-onix-white mb-6">{tool.title}</h3>
                      <p className="text-base leading-relaxed text-onix-silver mb-8 max-w-2xl">{tool.description}</p>
                      <div className="mb-8">
                        <p className="eyebrow mb-4">{tc.capacidades}</p>
                        <ul className="flex flex-wrap gap-2">
                          {tool.capabilities.map((c, j) => (
                            <li key={j} className="border border-onix-line px-3 py-1.5 text-xs text-onix-silver">
                              {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <DisclaimerBlock label={tc.limiteVisible}>{tool.limit}</DisclaimerBlock>
                      <Link
                        data-testid={`eco-link-${tool.slug}`}
                        to={`/herramientas/${tool.slug}`}
                        className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.14em] text-onix-white mt-8 group"
                      >
                        <span className="border-b border-onix-line pb-1 transition-colors group-hover:border-onix-blue">{tc.verCapacidad}</span>
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" />
                      </Link>
                    </div>
                  </article>
                </Reveal>
              </div>
            </CinematicScene>
          );
        })}
      </section>

      <CinematicScene img="/assets/img/onix_scene_5.webp" imgMobile="/assets/img/onix_scene_5_m.webp">
        <CTASection
          index="03"
          label={tc.contactoLabel}
          title={te.ctaTitle}
          text={te.ctaText}
          buttonLabel={tc.solicitarDemo}
          to="/contacto"
        />
      </CinematicScene>
    </main>
  );
}
